class AnnouncementBar extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['slide']
  }

  static get requiredAttributes () {
    return [
      'data-autoplay',
      'data-slide-duration',
      'data-position',
    ]
  }

  constructor () {
    super()

    this.currentIndex = 0
    this.interval = null
    this.resizeObserver = null

    this.isPausedByHover = false
    this.isPausedByFocus = false
    this.isPausedByEditor = false

    this.handleMouseEnter = this.handleMouseEnter.bind(this)
    this.handleMouseLeave = this.handleMouseLeave.bind(this)
    this.handleFocusIn = this.handleFocusIn.bind(this)
    this.handleFocusOut = this.handleFocusOut.bind(this)
    this.handleVisibilityChange =
      this.handleVisibilityChange.bind(this)
    this.handleReducedMotionChange =
      this.handleReducedMotionChange.bind(this)
    this.handleBlockSelect = this.handleBlockSelect.bind(this)
    this.handleBlockDeselect =
      this.handleBlockDeselect.bind(this)
    this.updateAnnouncementBarHeight =
      this.updateAnnouncementBarHeight.bind(this)
  }

  connectedCallback () {
    super.connectedCallback()

    this.start()
  }

  disconnectedCallback () {
    this.stop()

    if (this.resizeObserver !== null) {
      this.resizeObserver.disconnect()
      this.resizeObserver = null
    }

    document.documentElement.style.removeProperty(
      '--AnnouncementBar_Height',
    )
    document.documentElement.style.removeProperty(
      '--AnnouncementBar_MeasuredHeight',
    )
    document.documentElement.classList.remove(
      'has-announcement',
      'has-sticky-announcement',
    )

    super.disconnectedCallback()
  }

  setupListeners () {
    this.slides = this.els.slide.elements

    this.reducedMotionMedia = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    )

    this.currentIndex = this.getInitialSlideIndex()

    this.setActiveSlide(this.currentIndex)

    this.listeners.add(
      this,
      'mouseenter',
      this.handleMouseEnter,
    )

    this.listeners.add(
      this,
      'mouseleave',
      this.handleMouseLeave,
    )

    this.listeners.add(
      this,
      'focusin',
      this.handleFocusIn,
    )

    this.listeners.add(
      this,
      'focusout',
      this.handleFocusOut,
    )

    this.listeners.add(
      document,
      'visibilitychange',
      this.handleVisibilityChange,
    )

    this.listeners.add(
      this.reducedMotionMedia,
      'change',
      this.handleReducedMotionChange,
    )

    if (window.Shopify?.designMode) {
      this.listeners.add(
        document,
        'shopify:block:select',
        this.handleBlockSelect,
      )

      this.listeners.add(
        document,
        'shopify:block:deselect',
        this.handleBlockDeselect,
      )
    }

    document.documentElement.classList.add('has-announcement')

    if (this.position === 'sticky') {
      document.documentElement.classList.add(
        'has-sticky-announcement',
      )
    }

    this.resizeObserver = new ResizeObserver(
      this.updateAnnouncementBarHeight,
    )

    this.resizeObserver.observe(this)

    this.updateAnnouncementBarHeight()
  }

  start () {
    this.stop()

    if (!this.shouldAutoplay) return

    this.interval = window.setInterval(() => {
      this.showNextSlide()
    }, this.slideDuration)
  }

  stop () {
    if (this.interval === null) return

    window.clearInterval(this.interval)

    this.interval = null
  }

  restart () {
    this.stop()
    this.start()
  }

  showNextSlide () {
    const nextIndex =
      (this.currentIndex + 1) % this.slides.length

    this.setActiveSlide(nextIndex)
  }

  /**
   * @param {number} nextIndex
   * @returns {void}
   */
  setActiveSlide (nextIndex) {
    if (!this.slides[nextIndex]) return

    this.slides.forEach((slide, index) => {
      const isActive = index === nextIndex

      slide.classList.toggle(
        'announcement-bar__announcement--active',
        isActive,
      )

      slide.setAttribute(
        'aria-hidden',
        isActive ? 'false' : 'true',
      )
    })

    this.currentIndex = nextIndex
  }

  updateAnnouncementBarHeight () {
    const measuredHeight = this.offsetHeight
    const announcementBarHeight =
      this.position === 'sticky'
        ? measuredHeight
        : 0

    document.documentElement.style.setProperty(
      '--AnnouncementBar_Height',
      `${announcementBarHeight}px`,
    )
    document.documentElement.style.setProperty(
      '--AnnouncementBar_MeasuredHeight',
      `${measuredHeight}px`,
    )
  }

  handleMouseEnter () {
    this.isPausedByHover = true

    this.stop()
  }

  handleMouseLeave () {
    this.isPausedByHover = false

    this.start()
  }

  handleFocusIn () {
    this.isPausedByFocus = true

    this.stop()
  }

  /**
   * @param {FocusEvent} event
   * @returns {void}
   */
  handleFocusOut (event) {
    if (
      event.relatedTarget instanceof Node &&
      this.contains(event.relatedTarget)
    ) {
      return
    }

    this.isPausedByFocus = false

    this.start()
  }

  handleVisibilityChange () {
    if (document.hidden) {
      this.stop()

      return
    }

    this.start()
  }

  handleReducedMotionChange () {
    this.restart()
  }

  /**
   * @param {CustomEvent} event
   * @returns {void}
   */
  handleBlockSelect (event) {
    const selectedSlide = event.target.closest(
      '[data-announcement-bar-el="slide"]',
    )

    if (
      selectedSlide === null ||
      !this.contains(selectedSlide)
    ) {
      return
    }

    const selectedIndex =
      this.slides.indexOf(selectedSlide)

    if (selectedIndex === -1) return

    this.isPausedByEditor = true

    this.stop()
    this.setActiveSlide(selectedIndex)
  }

  /**
   * @param {CustomEvent} event
   * @returns {void}
   */
  handleBlockDeselect (event) {
    const deselectedSlide = event.target.closest(
      '[data-announcement-bar-el="slide"]',
    )

    if (
      deselectedSlide === null ||
      !this.contains(deselectedSlide)
    ) {
      return
    }

    this.isPausedByEditor = false

    this.start()
  }

  /**
   * @returns {number}
   */
  getInitialSlideIndex () {
    const activeSlideIndex = this.slides.findIndex(
      slide => slide.classList.contains(
        'announcement-bar__announcement--active',
      ),
    )

    return activeSlideIndex === -1
      ? 0
      : activeSlideIndex
  }

  /**
   * @param {string} key
   * @param {number} fallback
   * @returns {number}
   */
  getNumberFromDataset (key, fallback) {
    const value = Number.parseInt(
      this.dataset[key],
      10,
    )

    return Number.isNaN(value)
      ? fallback
      : value
  }

  get autoplay () {
    return this.dataset.autoplay === 'true'
  }

  get position () {
    return this.dataset.position
  }

  get slideDuration () {
    return this.getNumberFromDataset(
      'slideDuration',
      5000,
    )
  }

  get shouldAutoplay () {
    return (
      this.autoplay &&
      this.slides.length > 1 &&
      !this.reducedMotionMedia.matches &&
      !this.isPausedByHover &&
      !this.isPausedByFocus &&
      !this.isPausedByEditor &&
      !document.hidden
    )
  }
}

if (!customElements.get('announcement-bar')) {
  customElements.define(
    'announcement-bar',
    AnnouncementBar,
  )
}
